/**
 * ConnectRepoForm
 * Form for connecting a new GitHub repository to analyze
 * Includes RAG embedding model selection for vectorization
 */

import React, { useState, useEffect } from 'react';
import { Button, Dropdown } from '../../ui';
import { developerAgentApi } from '../../../services/api';
import api from '../../../services/api';
import type { EmbeddingModelInfo, EmbeddingConfig } from '../../../services/api';
import type { GitHubOrg, GitHubRepo } from '../../../types';
import styles from './ConnectRepoForm.module.css';

export interface ConnectRepoFormProps {
  onAnalyzeStart?: (repoFullName: string, analysisId: string) => void;
  onError?: (error: string) => void;
}

export const ConnectRepoForm: React.FC<ConnectRepoFormProps> = ({
  onAnalyzeStart,
  onError,
}) => {
  // State
  const [orgs, setOrgs] = useState<GitHubOrg[]>([]);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [selectedRepo, setSelectedRepo] = useState<string>('');
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // RAG Model State
  const [embeddingConfig, setEmbeddingConfig] = useState<EmbeddingConfig | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Load embedding models on mount
  useEffect(() => {
    const loadEmbeddingModels = async () => {
      setIsLoadingModels(true);
      try {
        const response = await api.getEmbeddingConfig();
        if (response.success && response.data) {
          setEmbeddingConfig(response.data);
          // Default to current model
          setSelectedModel(response.data.current.model);
        }
      } catch (err) {
        console.error('Failed to load embedding models:', err);
      } finally {
        setIsLoadingModels(false);
      }
    };
    loadEmbeddingModels();
  }, []);

  // Load GitHub organizations
  useEffect(() => {
    const loadOrgs = async () => {
      setIsLoadingOrgs(true);
      try {
        const response = await developerAgentApi.getGitHubOrgs();
        if (response.success && response.data) {
          setOrgs(response.data);
        }
      } catch (err) {
        console.error('Failed to load GitHub organizations:', err);
        onError?.('Failed to load GitHub organizations');
      } finally {
        setIsLoadingOrgs(false);
      }
    };
    loadOrgs();
  }, [onError]);

  // Load repositories when org is selected
  useEffect(() => {
    if (!selectedOrg) {
      setRepos([]);
      return;
    }

    const loadRepos = async () => {
      setIsLoadingRepos(true);
      setSelectedRepo('');
      try {
        const response = await developerAgentApi.getGitHubRepos(selectedOrg);
        if (response.success && response.data) {
          setRepos(response.data);
        }
      } catch (err) {
        console.error('Failed to load repositories:', err);
        onError?.('Failed to load repositories');
      } finally {
        setIsLoadingRepos(false);
      }
    };
    loadRepos();
  }, [selectedOrg, onError]);

  // Filter repos by search query
  const filteredRepos = repos.filter((repo) =>
    repo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    repo.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected model info
  const getSelectedModelInfo = (): EmbeddingModelInfo | undefined => {
    return embeddingConfig?.models.find((m) => m.model === selectedModel);
  };

  // Handle analyze button click
  const handleAnalyze = async () => {
    if (!selectedRepo) return;

    setIsAnalyzing(true);
    try {
      const repo = repos.find((r) => r.id === selectedRepo);
      if (!repo) return;

      // Build embedding model options
      const modelInfo = getSelectedModelInfo();
      const embeddingModel = modelInfo ? {
        provider: modelInfo.provider,
        model: modelInfo.model,
        dimensions: modelInfo.dimensions,
      } : undefined;

      const response = await developerAgentApi.analyzeRepo(repo.fullName, {
        includeRAG: true,
        embeddingModel,
      });
      if (response.success && response.data) {
        onAnalyzeStart?.(repo.fullName, response.data.analysisId);
        // Reset form
        setSelectedOrg('');
        setSelectedRepo('');
        setSearchQuery('');
      }
    } catch (err) {
      console.error('Failed to start analysis:', err);
      onError?.('Failed to start codebase analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Build model dropdown options grouped by provider
  const modelOptions = (embeddingConfig?.models || []).map((m) => ({
    value: m.model,
    label: `${m.model} (${m.provider}) — ${m.dimensions}d, $${m.pricePerMillionTokens}/M tokens`,
  }));

  return (
    <div className={styles.form}>
      <div className={styles.row}>
        {/* Organization Selector */}
        <div className={styles.field}>
          <label className={styles.label}>GitHub Organization</label>
          <Dropdown
            options={orgs.map((org) => ({
              value: org.login,
              label: org.name || org.login,
            }))}
            value={selectedOrg}
            onChange={setSelectedOrg}
            placeholder={isLoadingOrgs ? 'Loading organizations...' : 'Select organization'}
            disabled={isLoadingOrgs}
          />
        </div>

        {/* Repository Selector */}
        <div className={styles.field}>
          <label className={styles.label}>Repository</label>
          <div className={styles.repoSelector}>
            {selectedOrg && (
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isLoadingRepos}
              />
            )}
            <Dropdown
              options={filteredRepos.map((repo) => ({
                value: repo.id,
                label: repo.name,
              }))}
              value={selectedRepo}
              onChange={setSelectedRepo}
              placeholder={
                !selectedOrg
                  ? 'Select an organization first'
                  : isLoadingRepos
                  ? 'Loading repositories...'
                  : filteredRepos.length === 0 && searchQuery
                  ? 'No repositories match your search'
                  : 'Select repository'
              }
              disabled={!selectedOrg || isLoadingRepos}
            />
          </div>
          {selectedRepo && (
            <div className={styles.repoInfo}>
              {(() => {
                const repo = repos.find((r) => r.id === selectedRepo);
                return repo ? (
                  <>
                    {repo.description && (
                      <p className={styles.repoDescription}>{repo.description}</p>
                    )}
                    <div className={styles.repoMeta}>
                      {repo.language && <span className={styles.language}>{repo.language}</span>}
                      <span className={styles.visibility}>
                        {repo.private ? '🔒 Private' : '📖 Public'}
                      </span>
                    </div>
                  </>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* RAG Embedding Model Selector */}
        <div className={styles.field}>
          <label className={styles.label}>Embedding Model</label>
          <Dropdown
            options={modelOptions}
            value={selectedModel}
            onChange={setSelectedModel}
            placeholder={isLoadingModels ? 'Loading models...' : 'Select embedding model'}
            disabled={isLoadingModels}
          />
          {selectedModel && (
            <div className={styles.modelInfo}>
              {(() => {
                const model = getSelectedModelInfo();
                return model ? (
                  <>
                    <p className={styles.modelDescription}>{model.description}</p>
                    <div className={styles.modelMeta}>
                      <span className={styles.modelProvider}>{model.provider}</span>
                      <span className={styles.modelDimensions}>{model.dimensions}d</span>
                      <span className={styles.modelScore}>MTEB: {model.mtebScore}</span>
                    </div>
                    <div className={styles.modelTags}>
                      {model.bestFor.map((tag) => (
                        <span key={tag} className={styles.modelTag}>{tag}</span>
                      ))}
                    </div>
                  </>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Analyze Button */}
        <div className={styles.field}>
          <label className={styles.label}>&nbsp;</label>
          <Button
            onClick={handleAnalyze}
            disabled={!selectedRepo || isAnalyzing}
            variant="primary"
            className={styles.analyzeButton}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze Repository'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConnectRepoForm;
